import React from 'react'
import PropTypes from 'prop-types'
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  LinkBox,
  List,
  ListItem,
  Text
} from '@chakra-ui/react'
import { BulletIcon } from './BulletIcon'
import { VerticalLine } from './VerticalLine'
import { MenuItem } from './MenuItem'
import { TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons'
import { isActive } from '../../../utils/MenuHelper'
import { CgViewComfortable } from 'react-icons/cg'
import { useLocation, useNavigate } from 'react-router-dom'
import rootStore from '../../../store/RootStore'
import { observer } from 'mobx-react'
import { toJS } from 'mobx'

const MenuItems = React.memo(({ items, type, indent }) => {

  const {
    productsStore: { currentProduct },
    planStore: { currentPlan },
  } = rootStore

  if (currentProduct == 'pa' && indent >= 2) {
    return
  }

  return items.map((item, itemIdx) => {


    const isLastItem = itemIdx === items.length - 1
    const childItems = item.children || []

    const shouldRenderSubMenu = currentProduct == 'pa'
      ? false
      : childItems.some(child => {
        return child.componentType === 1
      })

    // const shouldRenderSubMenu = childItems.some(child => child.type !== 'chart')
    const MenuItemComponent = shouldRenderSubMenu ? MenuSection : MenuItem
    return (
      <MenuItemComponent
        key={itemIdx}
        title={item.name}
        id={item.id}
        items={childItems}
        type={type}
        index={itemIdx}
        indent={indent + 1}
        lastItem={isLastItem && !shouldRenderSubMenu}
      />
    )
  })
})

export const MenuSection = React.memo(props => {
  const {
    title,
    id,
    items,
    type,
    indent = 0,
    lastItem = false,
    header = false,
    index = 0,
  } = props

  const SectionComponent = type === 'table' ? MenuSectionTable : MenuSectionList

  const list = React.useMemo(
    () => (
      <MenuItems
        items={items}
        type={type}
        indent={indent}
        title={title}
      />
    ),
    [items, type, indent, title],
  )

  return (
    <SectionComponent
      title={title}
      id={id}
      items={items}
      header={header}
      lastItem={lastItem}
      indent={indent}
      list={list}
      type={type}
      index={index}
    />
  )
})

MenuSection.propTypes = {
  title: PropTypes.string,
  items: PropTypes.array,
  defaultIndex: PropTypes.number,
  type: PropTypes.string,
  header: PropTypes.bool,
  indent: PropTypes.number,
  index: PropTypes.number,
  lastItem: PropTypes.bool,
}

const MenuSectionList = observer(
  ({ title, id, items, header, lastItem, indent, list }) => {
    const {
      reportStore: { currentChartId, currentSectionId },
    } = rootStore

    // Check if this section or any child section is active
    let isItemActive = isActive(currentSectionId, { id, children: items })

    return (
      <Accordion
        reduceMotion
        allowMultiple
        m={'0 0'}
        p={0}
        position={'relative'}
        index={isItemActive ? [0] : undefined}>
        <AccordionItem border={0}>
          {({ isExpanded }) => (
            <>
              <AccordionButton>
                <Box
                  textAlign="left"
                  flexDirection="row"
                  alignItems={'center'}>
                  {!header ? (
                    <>
                      {!lastItem ||
                        isExpanded ||
                        (isItemActive && items.length > 0) ? (
                        <VerticalLine />
                      ) : null}
                      <BulletIcon active={isItemActive} />
                    </>
                  ) : null}
                  <Text
                    textStyle={`menuHeader${indent + 1}`}
                    color={isItemActive ? 'white' : 'whiteAlpha.500'}
                    pr={3}>
                    {title}
                  </Text>
                </Box>
                {indent === 0 ? (
                  <AccordionIcon
                    color="white"
                    fontSize="24px"
                    ml="12px"
                  />
                ) : isExpanded ? (
                  <TriangleUpIcon
                    color="white"
                    fontSize="8px"
                    ml={'12px'}
                    mr={'auto'}
                  />
                ) : (
                  <TriangleDownIcon
                    color="white"
                    fontSize="8px"
                    ml={'12px'}
                    mr={'auto'}
                    rotate="45deg"
                  />
                )}
              </AccordionButton>

              <AccordionPanel
                pb={2}
                pr={0}
                position="relative">
                <List>{list}</List>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>
    )
  },
)

const MenuSectionTable = observer(
  ({ title, id, items, header, index, indent, list }) => {
    const navigate = useNavigate()
    const location = useLocation()
    const {
      reportStore: { currentChartId, currentSectionId },
    } = rootStore

    // Check if this section or any child section is active
    let isItemActive = isActive(currentSectionId, { id, children: items })

    const isOverviewPage = location.pathname.indexOf('overview') > -1
    return (
      <Accordion
        allowMultiple
        m={'0 0'}
        p={0}
        position={'relative'}
        index={isItemActive ? [0] : undefined}>
        <AccordionItem border={0}>
          {({ isExpanded }) => (
            <>
              {!header && index !== 0 ? (
                <Box
                  position="absolute"
                  zIndex={0}
                  top={0}
                  left={0}>
                  <svg
                    width={'100%'}
                    height={2}>
                    <line
                      x1={0}
                      y1={0}
                      x2={999}
                      y2={0}
                      stroke="rgba(255, 255, 255, .1)"
                    />
                  </svg>
                </Box>
              ) : undefined}
              <AccordionButton>
                <Box
                  textAlign="left"
                  flexDirection="row"
                  alignItems={'center'}>
                  <Text
                    textStyle={`menuHeaderTable${indent + 1}`}
                    color={isItemActive ? 'white' : 'whiteAlpha.500'}
                    pr={3}
                    pt={1}
                    pb={1}>
                    {title}
                  </Text>
                </Box>
                {indent === 0 ? (
                  <AccordionIcon
                    color="white"
                    fontSize="24px"
                    ml="12px"
                  />
                ) : isExpanded ? (
                  <TriangleUpIcon
                    color="white"
                    fontSize="8px"
                    ml={'auto'}
                    mr={'0'}
                  />
                ) : (
                  <TriangleDownIcon
                    color="white"
                    fontSize="8px"
                    ml={'auto'}
                    mr={'0'}
                    rotate="45deg"
                  />
                )}
              </AccordionButton>

              <AccordionPanel
                pt={0}
                pb={2}
                pr={0}
                position="relative">
                <List>
                  {indent === 0 ? (
                    <ListItem
                      color={isOverviewPage ? 'white' : 'whiteAlpha.500'}
                      position={'relative'}
                      onClick={() =>
                        navigate(isOverviewPage ? '/reports' : '/overview')
                      }
                      _hover={{
                        background: 'blackAlpha.100',
                        color: 'white',
                      }}>
                      <LinkBox
                        cursor="pointer"
                        pl={4}>
                        <Flex
                          alignItems="center"
                          zIndex={1}
                          position="relative"
                          justifyContent={'space-between'}
                          pr={3}>
                          <Text
                            textStyle={'menuHeader3'}
                            whiteSpace={'nowrap'}
                            mb="12px"
                            mt={1}
                            pt={2}
                            pl={0}>
                            {window.location.href.indexOf('overview') > -1
                              ? 'Graphs'
                              : 'Overview'}
                          </Text>
                          <CgViewComfortable />
                        </Flex>
                      </LinkBox>
                    </ListItem>
                  ) : null}
                  {list}
                </List>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>
    )
  },
)

MenuSection.displayName = 'MenuSection'
